import { Num, Plural, T, Var, useGT } from 'gt-react';
import { TrendUpIcon } from '@phosphor-icons/react';
import { formatCurrency } from '@erp/shared-utils';
import type { CartForwardBuySummary } from '@erp/rpc';
import { cn } from '@/lib/utils';

/**
 * Forward buying on the open cart, inline in the cart header beside the MOV and
 * prepaid-freight meters (docs/procurement/forward-buying.md, "Board
 * surfaces"). It only summarizes; the lines themselves carry the base + added
 * detail, and the meters already say how far the cart is from its minimum.
 */

/**
 * The net spend change with its sign spelled out. The app's currency formatter
 * writes negatives in accounting parentheses, which would nest inside the
 * sentence's own, so the sign is added here and the formatter only sees the
 * magnitude.
 */
function formatSpendDelta(amount: number): string {
  return amount < 0
    ? `−${formatCurrency(-amount)}`
    : `+${formatCurrency(amount)}`;
}

/** Renders nothing unless a committed line in the cart carries an uplift. */
export function ForwardBuyCartNote({
  forwardBuy,
  className,
}: {
  forwardBuy: CartForwardBuySummary | null;
  className?: string;
}) {
  const gt = useGT();
  if (forwardBuy == null) return null;
  const lines = forwardBuy.linesUplifted;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-muted-foreground',
        className,
      )}
      title={gt('Forward buying adds {units} units across these lines', {
        context: 'replenishment forward-buy note tooltip: units added',
        units: forwardBuy.unitsAdded.toLocaleString(),
      })}
    >
      <TrendUpIcon aria-hidden className="h-3.5 w-3.5" />
      <T context="replenishment forward-buy summary on a vendor cart: how many lines the engine is buying further ahead, and the net change in the cart's spend">
        Forward-bought <Num>{lines}</Num>{' '}
        <Plural n={lines} one="line" other="lines" /> (
        <Var>{formatSpendDelta(forwardBuy.spendDelta)}</Var>)
      </T>
    </span>
  );
}
